;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:native-teams-frontend":true});}catch(e){}}();import {
  fetchPreparedIdempotentRequest,
  prepareIdempotentRequest,
  settleIdempotentRequest,
} from '@workspace/request-idempotency';
import {
  mergeNativeTeamsServerOutgoingFetchHeaders,
  mergeServerOutgoingFetchHeaders,
} from '@workspace/server-request-headers';
import { AUTH_SESSION_EXPIRY_COOKIE_KEY, createAuthSessionExpiryValue } from '@workspace/ui/lib';
import type { JwtPayload } from '@workspace/ui/types';
import { ApiError, type ApiResponse } from '@workspace/ui/types';
import { jwtDecode } from 'jwt-decode';
import { clearAuthCookies, refreshToken } from '@/actions/mixed/auth';
import { useAuthStore } from '@/stores/authStore';

const ALLOWED_AUTH_ORIGINS = [process.env.NEXT_PUBLIC_API_URL, process.env.NEXT_PUBLIC_SSO_API_URL]
  .filter(Boolean)
  .map((u) => {
    try {
      return new URL(u as string).origin;
    } catch {
      return null;
    }
  })
  .filter(Boolean) as string[];

function isAllowedAuthOrigin(target: string): boolean {
  try {
    const origin = new URL(target).origin;
    return ALLOWED_AUTH_ORIGINS.includes(origin);
  } catch {
    return false;
  }
}

type FetchOptions = RequestInit & {
  next?: {
    revalidate?: number | false;
    tags?: string[];
  };
  skipAuth?: boolean;
  skipIdempotency?: boolean;
  cache?: 'force-cache' | 'no-store';
  responseType?: 'json' | 'text' | 'blob' | 'arrayBuffer' | 'formData';
};

const URL_PATTERNS = {
  FULL_URL: /^https?:\/\//,
  API_ROUTE: /^\/api\//,
} as const;

// Client-side only: cache for client IP from /api/client-ip (so backend receives real IP when browser calls it directly)
let clientIpPromise: Promise<string | null> | null = null;

async function getClientIpFromOurApp(): Promise<string | null> {
  if (clientIpPromise !== null) return clientIpPromise;
  clientIpPromise = (async () => {
    try {
      const res = await fetch('/api/client-ip', { cache: 'no-store' });
      const data = (await res.json()) as { ip?: string | null };
      return data.ip ?? null;
    } catch {
      return null;
    }
  })();
  return clientIpPromise;
}

// Helper function to refresh token - delegates to auth.ts which handles locking
async function performTokenRefresh(
  shouldUseCurrentTokenIfProcessing = false
): Promise<string | undefined> {
  const { success, data, isProcessing } = await refreshToken();

  if (success && data && typeof data === 'object' && 'access_token' in data) {
    // refreshToken() already updates the store, just return the token
    return data.access_token as string;
  }
  if (isProcessing)
    return shouldUseCurrentTokenIfProcessing
      ? (useAuthStore.getState().accessToken ?? undefined)
      : undefined;

  throw new Error('Failed to refresh token');
}

// Helper function to clear auth cookies on server side
function clearServerAuthCookies(
  cookieStore: Awaited<ReturnType<typeof import('next/headers').cookies>>,
  getClearCookieConfig: () => ReturnType<typeof import('@/lib/auth-cookies').getClearCookieConfig>
) {
  try {
    cookieStore.set('access_token', '', getClearCookieConfig());
    cookieStore.set('refresh_token', '', getClearCookieConfig());
    cookieStore.set(AUTH_SESSION_EXPIRY_COOKIE_KEY, '', {
      ...getClearCookieConfig(),
      httpOnly: false,
    });
    cookieStore.set('2FA_REQUIRED', '', getClearCookieConfig());
    cookieStore.set('migrated_session', '', {
      ...getClearCookieConfig(),
      httpOnly: false,
      maxAge: 0,
    });
  } catch {
    // Ignore cookie setting errors in Server Components
  }
}

// Helper function to refresh token on server side
async function performServerTokenRefresh(): Promise<string | undefined> {
  try {
    const { cookies } = await import('next/headers');
    const { redirect } = await import('next/navigation');
    const {
      getAccessTokenCookieConfig,
      getAuthSessionExpiryCookieConfig,
      getClearCookieConfig,
      getRefreshTokenCookieConfig,
      getRefreshTokenMaxAge,
    } = await import('@/lib/auth-cookies');

    const cookieStore = await cookies();
    const refreshToken = cookieStore.get('refresh_token')?.value;

    if (!refreshToken) {
      console.error('Server: No refresh token available, redirecting to auth');
      clearServerAuthCookies(cookieStore, getClearCookieConfig);
      redirect(process.env.NEXT_PUBLIC_FE_URL_AUTH || 'http://localhost:3003');
    }

    // Call the backend SSO API to refresh the token
    const response = await fetch(`${process.env.NEXT_PUBLIC_SSO_API_URL}/refresh`, {
      method: 'POST',
      headers: await mergeNativeTeamsServerOutgoingFetchHeaders(
        {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        { method: 'POST' }
      ),
      body: JSON.stringify({ refresh_token: refreshToken }),
    });

    if (!response.ok) {
      console.error('Server: Failed to refresh token from SSO API, redirecting to auth');
      clearServerAuthCookies(cookieStore, getClearCookieConfig);
      redirect(process.env.NEXT_PUBLIC_FE_URL_AUTH || 'http://localhost:3003');
    }

    const data = await response.json();

    if (!data.data?.access_token) {
      console.error('Server: No access token in refresh response, redirecting to auth');
      clearServerAuthCookies(cookieStore, getClearCookieConfig);
      redirect(process.env.NEXT_PUBLIC_FE_URL_AUTH || 'http://localhost:3003');
    }

    const { access_token, refresh_token: new_refresh_token, expires_in } = data.data;

    // Calculate maxAge from JWT's exp field
    const nowSec = Math.floor(Date.now() / 1000);
    let accessTokenMaxAge = 900; // fallback: 15 minutes
    try {
      const decodedToken = jwtDecode<JwtPayload>(access_token);
      const exp = typeof decodedToken?.exp === 'number' ? decodedToken.exp : 0;
      accessTokenMaxAge = Math.max(exp - nowSec, 1);
    } catch {
      // keep fallback
    }

    // Try to set new cookies (will only work in Server Actions, not Server Components)
    try {
      cookieStore.set('access_token', access_token, getAccessTokenCookieConfig(accessTokenMaxAge));

      cookieStore.set(
        'refresh_token',
        new_refresh_token ?? refreshToken,
        getRefreshTokenCookieConfig(expires_in)
      );
      cookieStore.set(
        AUTH_SESSION_EXPIRY_COOKIE_KEY,
        createAuthSessionExpiryValue(getRefreshTokenMaxAge(expires_in)),
        getAuthSessionExpiryCookieConfig(expires_in)
      );
    } catch {
      // In Server Components during render, cookies are read-only
      // Just return the token without setting cookies
      // The token will be used for this request only
      console.log(
        'Server: Cannot set cookies in render context, returning token for current request'
      );
    }

    return access_token;
  } catch (error) {
    // Check if this is a Next.js redirect error (which is expected)
    if (error instanceof Error && error.message === 'NEXT_REDIRECT') {
      throw error; // Re-throw redirect errors
    }
    console.error('Server token refresh failed:', error);
    // If refresh fails completely, redirect to auth app
    const { redirect } = await import('next/navigation');
    redirect(process.env.NEXT_PUBLIC_FE_URL_AUTH || 'http://localhost:3003');
  }
}

// Helper function to get access token based on environment
async function getAccessToken(skipAuth: boolean): Promise<string | undefined> {
  if (skipAuth) return undefined;

  // Check if we're in a server environment
  if (typeof window === 'undefined') {
    try {
      // Dynamic import to avoid importing next/headers in client components
      const { cookies } = await import('next/headers');
      const cookieStore = await cookies();
      const cookie = cookieStore.get('access_token');
      const refreshTokenCookie = cookieStore.get('refresh_token');
      const accessToken = cookie ? cookie.value : undefined;

      // If no access token but refresh token exists, try to refresh
      if (!accessToken && refreshTokenCookie?.value) {
        console.log('Server: No access token but refresh token exists, refreshing');
        return await performServerTokenRefresh();
      }

      // Check token expiration on server side
      if (accessToken) {
        try {
          const decodedToken = jwtDecode<JwtPayload>(accessToken);
          const currentTime = Math.floor(Date.now() / 1000);
          const timeDifference = decodedToken.exp - currentTime;

          // If token is expired or expiring soon (< 60 seconds), refresh it
          if (timeDifference < 60) {
            console.log('Server: token is expired or expiring soon, refreshing it');
            // Only refresh if we have a refresh token
            if (refreshTokenCookie?.value) {
              return await performServerTokenRefresh();
            }
          }
        } catch {
          // If token decode fails (malformed token), try to refresh if refresh token exists
          console.error('Server: Failed to decode token, attempting refresh');
          if (refreshTokenCookie?.value) {
            return await performServerTokenRefresh();
          }

          // Without a refresh token, treat the access token as invalid
          return undefined;
        }
      }

      return accessToken;
    } catch (error) {
      console.error('Server: Error getting/refreshing token:', error);
      // If cookies() fails, we might be in a client context
      return undefined;
    }
  }

  try {
    const accessToken = useAuthStore.getState().accessToken;

    // console.log('getting access token in fetch-api.ts client side', accessToken);

    if (accessToken) {
      const decodedToken = jwtDecode<JwtPayload>(accessToken);
      const currentTime = Math.floor(Date.now() / 1000); // Current time in seconds
      const expirationTime = decodedToken.exp; // Expiration time in seconds
      const timeDifference = expirationTime - currentTime; // Difference in seconds

      if (timeDifference < 60) {
        console.log('token is expiring soon, refreshing it');
        // 1 minute in seconds - token is expiring soon, refresh it
        return await performTokenRefresh(true);
      }

      return accessToken;
    } else {
      // No access token - try to refresh
      return await performTokenRefresh();
    }
  } catch {
    await handleClientAuthError();
    return undefined;
  }
}

// Helper function to handle client-side authentication errors
async function handleClientAuthError() {
  // Client environment - clear store and redirect
  useAuthStore.getState().setAccessToken(null);
  useAuthStore.getState().setAuthenticated(false);
  useAuthStore.getState().setUser(null);

  try {
    await clearAuthCookies();
  } catch {
    // Cookie cleanup is best-effort. Local auth state is already cleared.
  }

  // Redirect to login
  window.location.href = '/login';
}

// Helper function to check if an error is a 401 auth error
export function isAuthError(error: unknown): error is ApiError {
  return error instanceof ApiError && error.status === 401;
}

export default async function fetchAPI<T = unknown>(
  url: string,
  options: FetchOptions = {}
): Promise<ApiResponse<T>> {
  const {
    skipAuth = false,
    skipIdempotency = false,
    cache = 'no-store',
    responseType = 'json',
    ...fetchOptions
  } = options;

  // Get access token based on environment
  const accessToken = await getAccessToken(skipAuth);

  // Detect if this is a full URL (starts with http:// or https://) or an API route (starts with /api/)
  const isFullUrl = URL_PATTERNS.FULL_URL.test(url);
  const isApiRoute = URL_PATTERNS.API_ROUTE.test(url);

  // For full URLs or API routes, use as-is
  // For relative paths, prepend the backend API URL
  const fullUrl = isFullUrl || isApiRoute ? url : `${process.env.NEXT_PUBLIC_API_URL}${url}`;

  // For full URLs or API routes, we don't need auth token in headers
  // For backend calls, we need auth token unless skipAuth is true
  if (!accessToken && !skipAuth && !isFullUrl && !isApiRoute) {
    throw new Error('No access token found');
  }

  // Auto-stringify plain objects
  const body =
    fetchOptions.body &&
    typeof fetchOptions.body === 'object' &&
    !(fetchOptions.body instanceof FormData)
      ? JSON.stringify(fetchOptions.body)
      : fetchOptions.body;

  const isJsonBody =
    typeof body === 'string' || (body && typeof body === 'object' && !(body instanceof FormData));

  const isBackendCall = !isApiRoute && (!isFullUrl || isAllowedAuthOrigin(url));

  let clientIp: string | undefined;

  if (isBackendCall) {
    if (typeof window === 'undefined') {
      try {
        const { headers: getHeaders } = await import('next/headers');
        const headersList = await getHeaders();
        const forwardedFor = headersList.get('x-forwarded-for');
        clientIp =
          forwardedFor?.split(',')[0]?.trim() ||
          headersList.get('x-real-ip') ||
          headersList.get('cf-connecting-ip') ||
          undefined;
      } catch {
        clientIp = undefined;
      }
    } else {
      const ip = await getClientIpFromOurApp();
      clientIp = ip ?? undefined;
    }
  }

  async function executeRequest(token?: string) {
    const outgoingHeaders = new Headers(fetchOptions.headers);
    if (!outgoingHeaders.has('Accept')) outgoingHeaders.set('Accept', 'application/json');
    if (isJsonBody && !outgoingHeaders.has('Content-Type'))
      outgoingHeaders.set('Content-Type', 'application/json');
    if (
      !outgoingHeaders.has('Authorization') &&
      !skipAuth &&
      token &&
      !isApiRoute &&
      (!isFullUrl || isAllowedAuthOrigin(url))
    ) {
      outgoingHeaders.set('Authorization', `Bearer ${token}`);
    }
    if (clientIp && isBackendCall && !outgoingHeaders.has('X-Forwarded-For')) {
      outgoingHeaders.set('X-Forwarded-For', clientIp);
    }
    const headers = await mergeServerOutgoingFetchHeaders(outgoingHeaders);
    const preparedRequest = await prepareIdempotentRequest({
      url: fullUrl,
      method: isBackendCall && !skipIdempotency ? fetchOptions.method : undefined,
      body,
      headers,
    });

    try {
      const response = await fetchPreparedIdempotentRequest(
        fullUrl,
        {
          ...fetchOptions,
          body,
          ...(typeof window === 'undefined' ? { cache } : {}),
          ...(typeof window === 'undefined' && options.next ? { next: options.next } : {}),
        },
        preparedRequest
      );
      return { preparedRequest, response };
    } catch (error) {
      settleIdempotentRequest(preparedRequest, { hasResponse: false });
      throw error;
    }
  }

  let { preparedRequest, response } = await executeRequest(accessToken);
  const canRetryAfterRefresh =
    typeof window !== 'undefined' &&
    response.status === 401 &&
    !skipAuth &&
    (isBackendCall || isApiRoute);

  if (canRetryAfterRefresh) {
    let hasRetriedAfterRefresh = false;
    settleIdempotentRequest(preparedRequest, {
      hasResponse: true,
      status: response.status,
    });

    try {
      const refreshedAccessToken = await performTokenRefresh();
      if (refreshedAccessToken) {
        ({ preparedRequest, response } = await executeRequest(refreshedAccessToken));
        hasRetriedAfterRefresh = true;
      }
    } catch (error) {
      await handleClientAuthError();
      throw error;
    }

    if (hasRetriedAfterRefresh && response.status === 401) await handleClientAuthError();
  }

  // Handle 204 No Content
  if (response.status === 204) {
    settleIdempotentRequest(preparedRequest, {
      hasResponse: true,
      status: response.status,
    });
    return {
      success: true,
      data: null as unknown as T,
    };
  }

  // Check if response is JSON (only for auto-detection when responseType is 'json')
  const contentType = response.headers.get('content-type');
  const isJson = contentType?.includes('application/json');

  // Handle non-JSON responses based on responseType
  if (!isJson || responseType !== 'json') {
    // Check for errors BEFORE parsing response body
    if (!response.ok) {
      // Try to parse error message from JSON if available
      let errorMessage = `HTTP error! status: ${response.status}`;
      let hasConsumedResponse = false;
      try {
        const errorData = (await response.json()) as ApiResponse;
        errorMessage = errorData.message || errorData.error || errorMessage;
        hasConsumedResponse = true;
      } catch {
        // If JSON parsing fails, try to get text
        try {
          const textError = await response.text();
          if (textError) errorMessage = textError;
          hasConsumedResponse = true;
        } catch {
          // Keep default error message
        }
      }
      if (hasConsumedResponse) {
        settleIdempotentRequest(preparedRequest, {
          hasResponse: true,
          status: response.status,
        });
      }

      throw new ApiError({
        message: errorMessage,
        status: response.status,
      });
    }

    let data: unknown;

    try {
      switch (responseType) {
        case 'text':
          data = await response.text();
          break;
        case 'blob':
          data = await response.blob();
          break;
        case 'arrayBuffer':
          data = await response.arrayBuffer();
          break;
        case 'formData':
          data = await response.formData();
          break;
        default:
          // Fallback to text for unknown types
          data = await response.text();
      }
    } catch {
      throw new ApiError({
        message: `Failed to parse response as ${responseType}`,
        status: response.status,
      });
    }
    settleIdempotentRequest(preparedRequest, {
      hasResponse: true,
      status: response.status,
    });

    return {
      success: true,
      data: data as T,
    };
  }

  // Handle JSON responses
  const data = (await response.json()) as ApiResponse<T>;
  settleIdempotentRequest(preparedRequest, {
    hasResponse: true,
    status: response.status,
  });

  if (!response.ok) {
    throw new ApiError({
      message: data.message || data.error || `HTTP error! status: ${response.status}`,
      status: response.status,
      error: data.error,
    });
  }

  // If the response is successful but contains an error message
  if (data.error || data.errors) {
    throw new ApiError({
      message: data.message || data.error || 'An error occurred',
      status: response.status,
      error: data.error,
    });
  }

  return data;
}
